import { Injectable } from '@angular/core';
import {
  ALGOLIA_CONFIG,
  ALGOLIA_INDICES,
} from 'src/app/core/config/algolia.config';
import algoliasearch, { SearchClient } from 'algoliasearch';
import { AlgoliaInsightsService } from './insights.service';
import { HttpClient } from '@angular/common/http';
import { API } from '@core/services/api.base';
import { take } from 'rxjs/operators';

export interface QuerySuggestion {
  objectID: string;
  query: string;
  popularity: number;
  nbHits: number;
  _highlightResult?: {
    query: {
      value: string;
      matchLevel: string;
      matchedWords: string[];
    };
  };
}

export interface AutocompleteItem {
  query: string;
  highlighted: string;
  popularity: number;
}

@Injectable({
  providedIn: 'root',
})
export class QuerySuggestionsService extends API {
  private readonly client: SearchClient;
  private readonly QUERY_SUGGESTIONS_INDEX =
    ALGOLIA_INDICES.BUSINESSES_QUERY_SUGGESTIONS;

  constructor(
    private readonly insightsService: AlgoliaInsightsService,
    http: HttpClient
  ) {
    super(http);
    this.client = algoliasearch(
      ALGOLIA_CONFIG.appId,
      ALGOLIA_CONFIG.searchApiKey
    );
  }

  /**
   * Search query suggestions
   */
  async searchSuggestions(
    query: string,
    limit: number = 5
  ): Promise<AutocompleteItem[]> {
    try {
      const index = this.client.initIndex(this.QUERY_SUGGESTIONS_INDEX);
      const result = await index.search(query, {
        hitsPerPage: limit,
        highlightPreTag: '<strong>',
        highlightPostTag: '</strong>',
      });

      if ('hits' in result && Array.isArray(result.hits)) {
        const hits = result.hits as unknown as QuerySuggestion[];

        return hits.map((hit) => ({
          query: hit.query,
          highlighted: hit._highlightResult?.query.value || hit.query,
          popularity: hit.popularity,
        }));
      }

      return [];
    } catch (error) {
      console.error('Error fetching query suggestions:', error);
      return [];
    }
  }

  /**
   * Track a user query to be added to suggestions index
   */
  async trackQuery(query: string, nbHits: number): Promise<void> {
    if (!query || query.trim().length === 0) {
      return;
    }

    try {
      const recentQueries = this.getRecentQueries();
      const newQuery = {
        query: query.trim().toLowerCase(),
        timestamp: Date.now(),
        nbHits,
      };

      const updatedQueries = [
        newQuery,
        ...recentQueries.filter((q) => q.query !== newQuery.query),
      ].slice(0, 50);
      localStorage.setItem(
        'algolia_recent_queries',
        JSON.stringify(updatedQueries)
      );

      await this.sendQueryToBackend(query, nbHits);

      console.log('Query tracked:', query, 'with', nbHits, 'hits');
    } catch (error) {
      console.error('Error tracking query:', error);
    }
  }

  /**
   * Get recent queries from local storage
   */
  getRecentQueries(): Array<{
    query: string;
    timestamp: number;
    nbHits: number;
  }> {
    try {
      const stored = localStorage.getItem('algolia_recent_queries');
      return stored ? JSON.parse(stored) : [];
    } catch {
      return [];
    }
  }

  /**
   * Get unique recent query strings for display
   */
  getRecentQueryStrings(limit: number = 10): string[] {
    const queries = this.getRecentQueries();
    return queries.slice(0, limit).map((q) => q.query);
  }

  /**
   * Send query to backend to be added to query suggestions index
   */
  private async sendQueryToBackend(
    query: string,
    nbHits: number
  ): Promise<void> {
    try {
      await this.post<void, { query: string; nbHits: number }>(
        'v1/business-information/query-suggestions/track',
        { query, nbHits }
      )
      .pipe(take(1))
      .toPromise();
      console.log('Query sent to backend:', query, 'with', nbHits, 'hits');
    } catch (error) {
      console.error('Error sending query to backend:', error);
    }
  }

  /**
   * Track search result click with query ID for analytics
   */
  trackResultClick(params: {
    query: string;
    queryID: string;
    objectID: string;
    position: number;
  }): void {
    this.insightsService.clickedObjectIDsAfterSearch({
      eventName: 'Business Clicked After Search',
      index: ALGOLIA_INDICES.BUSINESSES,
      queryID: params.queryID,
      objectIDs: [params.objectID],
      positions: [params.position],
    });
  }

  /**
   * Track conversion (e.g., booking made)
   */
  trackConversion(params: { queryID: string; objectIDs: string[] }): void {
    this.insightsService.convertedObjectIDsAfterSearch({
      eventName: 'Business Booking After Search',
      index: ALGOLIA_INDICES.BUSINESSES,
      queryID: params.queryID,
      objectIDs: params.objectIDs,
    });
  }
}
